import { flatten } from 'flat';
import { Translation } from 'types/language/translation.lang';

import React, { useContext, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { En } from 'translations/en';
import { Es } from 'translations/es';
import { Nb } from 'translations/nb';
import { Vn } from 'translations/vn';

export type Languages = 'en' | 'nb' | 'es' | 'vn';

type MessageOptions = {
  [key: string]: Translation;
};

const messages: MessageOptions = {
  en: En,
  nb: Nb,
  es: Es,
  vn: Vn,
};

type State = {
  language: string;
  changeLanguage(lang: Languages): void;
};

const SetInitialLanguage = () => {
  let initLang: string = localStorage.getItem('language') || navigator.language;

  return initLang in messages ? initLang : 'en';
};

type LanguageProviderProps = {
  children: React.ReactNode;
};

const LanguageContext = React.createContext<State | undefined>(undefined);

const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState(SetInitialLanguage());
  localStorage.setItem('language', language);

  const changeLanguage = (lang: Languages): void => {
    localStorage.setItem('language', lang);
    setLanguage(lang);
  };

  const stripLangCode = (lang: string) => lang.split(/[-_]/)[0];

  const getLocale = (lang: string) => {
    switch (lang) {
      case 'en':
        return 'en-gb';
      case 'es':
        return 'es-cl';
      default:
        return lang;
    }
  };

  // NOTE: We check if the language is spanish, and if so, set the locale to es-cl. FormatJs has support for chile locales (needed for number and date formatting when using <FormattedDate /> and <FormattedNumber />), but the language is spanish

  // NOTE: Maybe expose messages in LanguageContext and move IntlProvider into LocaleContext to handle setting locale and messages correctly

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      <IntlProvider
        locale={getLocale(language)}
        messages={flatten(messages[stripLangCode(language)] ? messages[stripLangCode(language)] : messages.en)}>
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
};

function useLanguageState() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useAccountState must be used within a AccountProvider');
  }
  return context;
}

export { LanguageProvider, useLanguageState };
